// utils/customerPaymentIdempotency.ts
//
// Explicit attempt-lifecycle state machine + canonical payload/fingerprint
// helpers for POST /ar-ap/customer-payments (manual/offline "Add Payment"),
// used by pages/SaleDetail.tsx -- the only frontend caller of this endpoint.
// Mirrors utils/fixedAssetPaymentIdempotency.ts's "idle" | "in-flight" |
// "ambiguous" design exactly, including its single-slot-scoped-by-resource-id
// shape: SaleDetail.tsx is mounted once for the `sales/:id` route and reused
// across different Sale/Invoice ids without remounting (confirmed directly:
// App.tsx registers a single `<Route path="sales/:id" element={<SaleDetail
// />} />`, and the existing `useEffect(() => { ...; setPaymentAmount(""); },
// [sale?.id])` already resets other payment-form state on a Sale change --
// but nothing previously reset the idempotency ref itself), exactly the
// same "component reused across a route-param change" scenario
// fixedAssetPaymentIdempotency.ts / vendorPaymentIdempotency.ts already
// solve for their own resource ids.
//
// Deliberately NOT a generic cross-domain refactor: this module duplicates
// the same shape (and much of the same prose) as the other three
// *Idempotency.ts files on purpose, matching this repository's own
// established convention of one small dedicated module per endpoint even
// when the underlying logic is structurally identical.
//
// NEW relative to every existing *Idempotency.ts module: sessionStorage
// persistence of ambiguous attempts (see "Persistence" section below).
// SaleDetail.tsx's payment section is not a modal -- if the page reloads
// mid-request or right after an ambiguous outcome, a bare in-memory ref
// (React state or otherwise) is destroyed with it, and the NEXT load would
// have no way to know a same-key retry could still safely replay the prior
// attempt. None of the existing endpoints' pages persist across reload
// today; this module adds it because Part 3 of this patch explicitly
// requires it for Customer Payment.
//
// Money scale: confirmed by direct repository search (this module's own
// audit, and the identical prior finding already documented in
// utils/depositCreateIdempotency.ts / utils/depositWriteoffIdempotency.ts /
// utils/depositRefundIdempotency.ts) that NO frontend file anywhere reads
// `money_scale` -- there is no frontend-visible company money-scale source
// to use instead. The one piece of direct, in-repo evidence for this exact
// call path is SaleDetail.tsx's own `calcFee` helper, which already
// hardcodes `Math.round(x * 100) / 100` (scale 2) for the very same fee
// this module fingerprints. Given no better source exists anywhere in the
// product, this module uses the same scale-2 assumption as every other
// existing idempotency module, rather than inventing a new source of
// truth. A company configured with a non-default money_scale is a
// pre-existing, repository-wide gap (also affecting the payment form's own
// display/rounding), not one newly introduced by this fingerprint.

const MONEY_SCALE_DECIMALS = 2;

/**
 * Normalizes a FEE-shaped input to a fixed-scale string. Omitted, blank,
 * non-numeric, and non-positive values all canonicalize to the same
 * "0.00" string as an explicit zero -- in this domain (and matching the
 * manual Customer Payment endpoint's own fee handling, where an omitted
 * `fee` field and an explicit `fee: 0` are posting-equivalent) there is no
 * distinct "negative or invalid" request identity to preserve for fee
 * specifically.
 *
 * NEVER use this for `amount` -- unlike fee, an invalid/blank/zero/negative
 * amount is not a legitimate economic value in this domain (the backend
 * itself rejects it: "Payment amount must be greater than zero"), so
 * treating it the same as "$0.00" would let a canonicalization bug silently
 * fingerprint a broken request as if it were a real, valid zero-amount
 * attempt. See toAmountScaleString, which fails explicitly instead.
 */
function toFeeScaleString(value: string | number | null | undefined): string {
  const n = typeof value === "number" ? value : parseFloat(String(value ?? "").trim());
  if (!Number.isFinite(n) || n <= 0) return (0).toFixed(MONEY_SCALE_DECIMALS);
  return n.toFixed(MONEY_SCALE_DECIMALS);
}

/**
 * Normalizes an AMOUNT input to a fixed-scale string. Unlike fee, there is
 * no valid "0.00" fallback for amount -- invalid, blank, non-numeric, zero,
 * and negative input all throw explicitly rather than silently
 * canonicalizing to a payload that looks like a legitimate zero-amount
 * payment attempt. canonicalCustomerPayment's only current caller
 * (SaleDetail.tsx's handleMarkPaid) already validates `payAmt > 0` before
 * ever calling this, so this throw is not expected to be reachable in
 * practice -- it exists so that canonicalization itself can never be the
 * one making an invalid amount look valid, independent of caller
 * discipline (e.g. a future caller, or a test, that skips validation).
 */
function toAmountScaleString(value: string | number): string {
  const n = typeof value === "number" ? value : parseFloat(String(value ?? "").trim());
  if (!Number.isFinite(n) || n <= 0) {
    throw new Error(
      `canonicalCustomerPayment: amount must be a positive finite number, got ${JSON.stringify(
        value,
      )}`,
    );
  }
  return n.toFixed(MONEY_SCALE_DECIMALS);
}

type CustomerPaymentPayloadCore = {
  invoice_id: number;
  payment_date: string;
  amount: string;
  payment_account_id: number;
  payment_method_id: number;
};

// FEE-RATE-SEMANTICS-1 Phase 2B (Condition 3 / Finding 5): the canonical
// identity is a GENUINE discriminated union. "configured" forbids a fee entirely
// (`fee?: never`) -- so a later configured rate/fixed-fee change cannot move a
// retry's identity; "explicit" REQUIRES the normalized fee (including "0.00" for
// cash). configured-plus-fee and explicit-without-fee are both non-constructible
// (proven in customerPaymentIdempotency.typecheck.ts). A pre-2B stored
// fingerprint has NO fee_mode key -- see isLegacyCanonicalFingerprint.
export type CustomerPaymentPayload = CustomerPaymentPayloadCore &
  (
    | { fee_mode: "configured"; fee?: never }
    | { fee_mode: "explicit"; fee: string }
  );

/** Posting fee-intent for the canonical identity. A bare legacy fee value
 * (number/string/null/undefined) is treated as "explicit" for back-compat. */
export type CustomerPaymentFeeIntent =
  | { mode: "configured" }
  | { mode: "explicit"; fee: string | number | null | undefined };

function isFeeIntent(v: unknown): v is CustomerPaymentFeeIntent {
  return !!v && typeof v === "object" && "mode" in (v as object);
}

/**
 * Deterministic canonical payload for POST /ar-ap/customer-payments.
 *
 * - invoice_id: canonicalized to a number -- the backend's request-identity
 *   comparison (services/payments.py's
 *   _assert_customer_payment_identity_compatible, added in 7a086205)
 *   treats a same-key retry naming a DIFFERENT invoice_id as a conflict,
 *   so this must be part of the fingerprint even though it is also used
 *   locally to scope which invoice's attempt slot a submission reads/
 *   writes (see CustomerPaymentAttempt.invoiceId below).
 * - payment_date: the exact submitted YYYY-MM-DD -- never re-derived from
 *   "today" at retry time (mirrors every other module's identical
 *   rationale: an implicit "today" fallback would make an ambiguous retry
 *   that crosses midnight recompute a different fingerprint and false-
 *   conflict).
 * - amount: normalized via toAmountScaleString -- see module docstring for
 *   why scale 2 is used. Throws explicitly for invalid/blank/zero/negative
 *   input rather than silently producing "0.00" (see toAmountScaleString);
 *   callers must validate amount > 0 before calling this, exactly as
 *   SaleDetail.tsx's handleMarkPaid already does.
 * - fee: normalized via toFeeScaleString (zero-defaulting -- see that
 *   function's docstring for why this is safe for fee but not for
 *   amount). Part of backend request identity for this endpoint
 *   specifically: 7a086205 folds `fee` into the SAME identity comparison
 *   as amount/date/account/method for any NON-verified-Stripe caller, and
 *   the manual endpoint this page calls is always non-verified. Fee must
 *   therefore never be omitted from this fingerprint -- an omitted-fee
 *   canonical payload would treat a fee-only edit as unchanged, silently
 *   reusing a key the backend will reject.
 * - payment_account_id / payment_method_id: canonicalized to numbers so
 *   "12" and 12 never produce different fingerprints.
 */
export function canonicalCustomerPayment(
  invoiceId: string | number,
  paymentDate: string,
  amount: string | number,
  paymentAccountId: string | number,
  paymentMethodId: string | number,
  feeOrIntent: string | number | null | undefined | CustomerPaymentFeeIntent,
): CustomerPaymentPayload {
  const core = {
    invoice_id: Number(invoiceId),
    payment_date: paymentDate,
    amount: toAmountScaleString(amount),
    payment_account_id: Number(paymentAccountId),
    payment_method_id: Number(paymentMethodId),
  };
  // A bare fee argument (legacy callers) is treated as explicit intent.
  const intent: CustomerPaymentFeeIntent = isFeeIntent(feeOrIntent)
    ? feeOrIntent
    : { mode: "explicit", fee: feeOrIntent };
  if (intent.mode === "configured") {
    // No fee key at all -- the configured identity is rate-independent.
    return { ...core, fee_mode: "configured" };
  }
  return { ...core, fee_mode: "explicit", fee: toFeeScaleString(intent.fee) };
}

export function fingerprintCustomerPayment(payload: CustomerPaymentPayload): string {
  return JSON.stringify(payload);
}

/**
 * True iff `fingerprint` is a pre-2B ("old-shape") canonical identity: a
 * parseable JSON object with NO `fee_mode` discriminator. Used so a retry of a
 * fingerprint minted by an earlier app version is surfaced with distinct,
 * non-misleading guidance (an identical-details retry can never match it, since
 * the new canonical identity always carries fee_mode). Unparseable/foreign
 * strings return false -- they fall through to the normal mismatch block rather
 * than being claimed as a recognized old shape.
 */
export function isLegacyCanonicalFingerprint(
  fingerprint: string | null | undefined,
): boolean {
  if (!fingerprint) return false;
  try {
    const obj = JSON.parse(fingerprint);
    return !!obj && typeof obj === "object" && !("fee_mode" in obj);
  } catch {
    return false;
  }
}

export function randomUuidKey(): string {
  return crypto.randomUUID();
}

/**
 * Customer Payment's idempotency key format: `ui:customer_payment:<invoice_id>:<uuid>`.
 * The invoice_id embedded here is cosmetic/for human-readable uniqueness
 * only -- the backend never parses the key string, it only compares the
 * ACTUAL invoice_id field in the request body against the existing row's
 * invoice_id (see canonicalCustomerPayment's doc above). It is still
 * generated from the SAME invoiceId the attempt is scoped to, so two
 * different invoices' freshly-minted keys never collide by construction.
 */
export function customerPaymentIdempotencyKey(invoiceId: number): string {
  return `ui:customer_payment:${invoiceId}:${randomUuidKey()}`;
}

// ---------------------------------------------------------------------------
// Attempt lifecycle
// ---------------------------------------------------------------------------

/**
 * - "idle": no active attempt for this invoice. The next submission always
 *   mints a fresh key.
 * - "in-flight": a request has been sent for this invoice and its outcome
 *   is not yet known. Transitions to "idle" (success or definite
 *   rejection) or "ambiguous" once the response (or its absence) is
 *   classified. Never itself persisted to sessionStorage (see
 *   StoredCustomerPaymentAttempt) -- see Part 3 reasoning below.
 * - "ambiguous": this invoice's last attempt outcome could not be
 *   determined (network failure, timeout, unreadable response, HTTP 500,
 *   or any other unexpected server error). The key/fingerprint are
 *   retained (in memory AND in sessionStorage) so an unchanged retry
 *   safely replays instead of risking a duplicate; an edited retry stays
 *   blocked until this specific attempt is resolved. A DIFFERENT
 *   invoice's own attempt is entirely unaffected.
 */
export type CustomerPaymentAttemptState = "idle" | "in-flight" | "ambiguous";

export type CustomerPaymentAttempt = {
  state: CustomerPaymentAttemptState;
  invoiceId: number | null;
  idempotencyKey: string | null;
  submittedFingerprint: string | null;
  submittedPayload: CustomerPaymentPayload | null;
};

export const IDLE_CUSTOMER_PAYMENT_ATTEMPT: CustomerPaymentAttempt = {
  state: "idle",
  invoiceId: null,
  idempotencyKey: null,
  submittedFingerprint: null,
  submittedPayload: null,
};

export type CustomerPaymentSubmitDecision =
  | { kind: "start"; idempotencyKey: string; attempt: CustomerPaymentAttempt }
  | { kind: "blocked-mismatch" }
  | { kind: "blocked-legacy-shape" }
  | { kind: "blocked-cross-invoice" }
  | { kind: "blocked-in-flight" };

/**
 * Pure decision for whether a submit attempt may proceed to the network,
 * given the CURRENT attempt state (the caller is responsible for looking
 * up/restoring THIS invoice's own slot before calling this -- see
 * SaleDetail.tsx's restore effect), the invoice being paid, and the
 * canonical payload/fingerprint just built from the form. Never mutates
 * `attempt` -- returns a NEW attempt (state "in-flight") for the caller to
 * store when `kind === "start"`.
 *
 *  - idle -> always starts a fresh attempt with a newly generated key.
 *  - ambiguous, SAME invoice, SAME fingerprint -> unchanged retry: reuses
 *    the stored key exactly (never mints a new one for an ambiguous
 *    outcome).
 *  - ambiguous, SAME invoice, DIFFERENT fingerprint -> blocked-mismatch:
 *    the prior attempt under this key may already have committed; an
 *    edited resubmission under the same key must never be sent.
 *  - ambiguous, DIFFERENT invoice (including a malformed/stale attempt
 *    that names some other invoiceId) -> blocked-cross-invoice: never
 *    reused, never silently rotated to a fresh key on this caller's
 *    behalf -- the caller must resolve or explicitly discard the other
 *    invoice's attempt first.
 *  - in-flight -> blocked-in-flight: defensive. The component's own
 *    synchronous in-flight ref already prevents this from being reached
 *    in practice, but the decision stays correct even if called before
 *    that guard, or from a test, without it.
 */
export function decideCustomerPaymentAttempt(
  attempt: CustomerPaymentAttempt,
  invoiceId: number,
  payload: CustomerPaymentPayload,
  fingerprint: string,
  generateKey: () => string,
): CustomerPaymentSubmitDecision {
  if (attempt.state === "in-flight") {
    return { kind: "blocked-in-flight" };
  }

  if (attempt.state === "ambiguous") {
    if (attempt.invoiceId !== invoiceId) {
      return { kind: "blocked-cross-invoice" };
    }
    if (attempt.submittedFingerprint !== fingerprint) {
      // A pre-2B (old-shape) stored fingerprint can never equal a current-shape
      // one -- even for identical visible details -- so it must NOT be offered
      // the normal "retry unchanged to resolve" guidance. Surface it distinctly.
      // Either block leaves the stored key/fingerprint untouched (never rebound
      // or cleared, never a fresh key).
      if (isLegacyCanonicalFingerprint(attempt.submittedFingerprint)) {
        return { kind: "blocked-legacy-shape" };
      }
      return { kind: "blocked-mismatch" };
    }
    return {
      kind: "start",
      idempotencyKey: attempt.idempotencyKey as string,
      attempt: { ...attempt, state: "in-flight" },
    };
  }

  const idempotencyKey = generateKey();
  return {
    kind: "start",
    idempotencyKey,
    attempt: {
      state: "in-flight",
      invoiceId,
      idempotencyKey,
      submittedFingerprint: fingerprint,
      submittedPayload: payload,
    },
  };
}

/** Confirmed 2xx success (create or successful replay -- both return the
 * same response shape, so no distinction is needed here). Clears the
 * attempt back to idle. Caller must also clear sessionStorage for this
 * invoice (see clearStoredCustomerPaymentAttempt). */
export function resolveCustomerPaymentSuccess(): CustomerPaymentAttempt {
  return IDLE_CUSTOMER_PAYMENT_ATTEMPT;
}

/**
 * Definite pre-commit rejection: any deterministic 4xx this endpoint's
 * stack can produce -- 400/404 raised directly by ar_ap_router.py's
 * create_customer_payment or services/payments.py's
 * record_customer_payment (invoice/payment-method/account not found,
 * non-positive amount, exceeds outstanding balance, Stripe-online
 * rejection), 401/403 raised by get_current_user before the handler body
 * ever runs, 422 from FastAPI's own request-validation short-circuit, a
 * closed-period 409 (assert_year_open, called before any row is touched),
 * or an identity/reconciliation 409 (_assert_customer_payment_identity_
 * compatible, run before any fee repair/provider enrichment/JE repair/
 * status refresh -- see 7a086205). In every one of these cases nothing
 * NEW was persisted under this key, so the identity is free to reuse.
 * Returns to idle so the next submission mints a fresh key. Caller must
 * also clear sessionStorage for this invoice.
 */
export function resolveCustomerPaymentDefiniteRejection(): CustomerPaymentAttempt {
  return IDLE_CUSTOMER_PAYMENT_ATTEMPT;
}

/**
 * Ambiguous outcome: network failure, timeout, a lost/malformed/unreadable
 * response, HTTP 500, or any other unexpected server error where the
 * operation may have committed before the response was constructed.
 * Retains the key, fingerprint, and submitted payload so a same-invoice
 * unchanged retry safely replays instead of risking a duplicate; an
 * edited retry -- or a retry against a different invoice -- stays
 * blocked. Caller must also persist this to sessionStorage (see
 * writeStoredCustomerPaymentAttempt) -- though in practice the ambiguous-
 * shaped record is written to storage BEFORE the request is even sent
 * (Part 3), so this in-memory transition and storage are already
 * consistent by the time this resolver is called.
 */
export function resolveCustomerPaymentAmbiguousFailure(
  attempt: CustomerPaymentAttempt,
): CustomerPaymentAttempt {
  return { ...attempt, state: "ambiguous" };
}

/**
 * Whether SaleDetail.tsx's payment section should treat this invoice as
 * having a live, unresolved ambiguous attempt -- used both for the initial
 * mount/reload restore effect and whenever the resolved invoice_id changes
 * for the currently-mounted component (Sale A -> Sale B navigation without
 * a remount). True only for an ambiguous attempt on THIS SAME invoiceId.
 */
export function shouldRestoreAmbiguousAttempt(
  attempt: CustomerPaymentAttempt,
  invoiceId: number,
): boolean {
  return attempt.state === "ambiguous" && attempt.invoiceId === invoiceId;
}

// ---------------------------------------------------------------------------
// Failure classification
// ---------------------------------------------------------------------------

export type CustomerPaymentFailureClass =
  | "definite-rejection"
  | "closed-period"
  | "idempotency-conflict"
  | "ambiguous";

/**
 * Classifies a thrown apiPost error into one of the four outcomes this
 * endpoint can produce. Deliberately does NOT classify by HTTP status
 * alone for 409: a closed-period rejection and BOTH backend 409 varieties
 * introduced by 7a086205 (the plain request-identity conflict raised by
 * _assert_customer_payment_identity_compatible, and
 * CustomerPaymentReconciliationConflict) share HTTP 409 but require
 * different messaging -- a closed-period 409 is detected via
 * `isClosedPeriod`'s message match. Explicit Stripe safety codes retain
 * the retry identity; remaining legacy 409s are idempotency conflicts.
 * Note: CustomerPaymentReconciliationConflict can
 * only ever be raised when record_customer_payment's verified_stripe
 * argument is True, and the manual /ar-ap/customer-payments endpoint this
 * page calls never sets it -- so each remaining non-safety, non-period 409
 * this specific caller can receive is the plain identity conflict, never
 * the reconciliation one. The classifier still folds both into the same
 * "idempotency-conflict" bucket (rather than assuming only one is
 * reachable) so it stays correct if that ever changes.
 *
 * Every OTHER 4xx status this endpoint's stack can actually produce --
 * 400/404 raised directly by the handler or record_customer_payment, 401/
 * 403 raised by get_current_user (resolves as a FastAPI dependency BEFORE
 * the handler body runs at all), and 422 from FastAPI's own
 * RequestValidationError handler (also short-circuits before the handler
 * runs) -- is treated as a definite rejection uniformly: every one of
 * these occurs strictly before any business write for a FRESH request, so
 * nothing new was ever persisted under the key. (A 404 reached via the
 * REPLAY path, e.g. the existing row's invoice having since become
 * unlocatable, is a rare edge case not otherwise distinguished here --
 * clearing the key and minting a fresh one on the next attempt cannot
 * corrupt the old row, only abandon it, so folding it into
 * "definite-rejection" remains the safe choice.)
 *
 * `status` (added to apiPost's thrown error by api/client.ts) is what
 * distinguishes a genuine HTTP response (4xx/5xx) from a network-level
 * failure: fetch() itself rejecting (network down, DNS, CORS, or a
 * connection-level timeout) throws a plain Error/TypeError with no
 * `.response` at all, so `err?.response?.status` is `undefined` for
 * those -- correctly folded into "ambiguous" here, the same as a
 * malformed/unreadable response. 5xx (500 and any other): ambiguous --
 * record_customer_payment's writes commit only via the CALLER's db.commit()
 * after it returns, but a 500 raised anywhere in that surrounding request
 * handling (including after a successful insert) can still mean the
 * payment WAS persisted. Fails closed for anything not positively
 * identified as a definite pre-commit rejection.
 */
export function classifyCustomerPaymentFailure(
  err: any,
  isClosedPeriod: (err: any) => boolean,
): CustomerPaymentFailureClass {
  const status = err?.response?.status;

  if (typeof status !== "number") {
    return "ambiguous";
  }

  // Safety refusals retain the receipt identity just like an unresolved retry.
  // They are not evidence that this key belongs to a different payment.
  const code = err?.response?.data?.detail?.code;
  if (["stripe_payment_in_progress", "stripe_page_unconfirmed",
       "stripe_receipt_needs_reconciliation"].includes(code)) {
    return "ambiguous";
  }

  if (status === 409) {
    return isClosedPeriod(err) ? "closed-period" : "idempotency-conflict";
  }

  if (status >= 400 && status < 500) {
    return "definite-rejection";
  }

  return "ambiguous";
}

// ---------------------------------------------------------------------------
// Persistence (sessionStorage) -- NEW relative to every other
// *Idempotency.ts module. See module docstring and Part 3 of this patch.
// ---------------------------------------------------------------------------

/**
 * Persisted shape for an ambiguous Customer Payment attempt. Deliberately
 * excludes the raw submitted payload (amount/date/account/method/fee) --
 * the fingerprint alone is sufficient to detect an edited retry, and
 * storing the raw values would duplicate request data in sessionStorage
 * for no correctness benefit. `status` is a literal "ambiguous" (not a
 * union with "in-flight") on purpose: nothing in this module ever writes
 * an "in-flight"-shaped record to storage at all (see
 * writeStoredCustomerPaymentAttempt's call site in SaleDetail.tsx, which
 * writes BEFORE the request is sent, already in this exact ambiguous
 * shape) -- so a reload can never restore "in-flight" simply because
 * nothing with that status is ever persisted.
 */
export type StoredCustomerPaymentAttempt = {
  status: "ambiguous";
  invoiceId: number;
  idempotencyKey: string;
  fingerprint: string;
};

export function customerPaymentAttemptStorageKey(
  companyId: number,
  invoiceId: number,
): string {
  return `nimos:customer-payment-attempt:${companyId}:${invoiceId}`;
}

function isStoredCustomerPaymentAttempt(
  value: unknown,
  invoiceId: number,
): value is StoredCustomerPaymentAttempt {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    v.status === "ambiguous" &&
    typeof v.invoiceId === "number" &&
    v.invoiceId === invoiceId &&
    typeof v.idempotencyKey === "string" &&
    v.idempotencyKey.length > 0 &&
    typeof v.fingerprint === "string" &&
    v.fingerprint.length > 0
  );
}

/**
 * Reads and validates the stored ambiguous attempt for (companyId,
 * invoiceId), if any. Returns null for: nothing stored, unparsable JSON,
 * or a parsed value that doesn't match the expected shape / doesn't
 * actually belong to this invoiceId (defense in depth beyond the storage
 * key's own scoping -- guards against a hand-edited or otherwise
 * corrupted entry). Malformed entries are actively removed here (not just
 * ignored) so a permanently-broken record can never linger and re-fail
 * every future read. Never throws -- sessionStorage access itself can
 * fail (private browsing quota, disabled storage), which is treated the
 * same as "nothing stored": persistence is a best-effort durability
 * improvement, not a correctness requirement, since the in-memory attempt
 * ref still protects the current tab for as long as it stays mounted.
 */
export function readStoredCustomerPaymentAttempt(
  companyId: number,
  invoiceId: number,
): StoredCustomerPaymentAttempt | null {
  const key = customerPaymentAttemptStorageKey(companyId, invoiceId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem(key);
      return null;
    }

    if (!isStoredCustomerPaymentAttempt(parsed, invoiceId)) {
      sessionStorage.removeItem(key);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/** Persists an ambiguous attempt for (companyId, attempt.invoiceId). Only
 * ever called with an ambiguous-shaped record -- see module docstring. */
export function writeStoredCustomerPaymentAttempt(
  companyId: number,
  attempt: StoredCustomerPaymentAttempt,
): void {
  try {
    sessionStorage.setItem(
      customerPaymentAttemptStorageKey(companyId, attempt.invoiceId),
      JSON.stringify(attempt),
    );
  } catch {
    // Best-effort -- see readStoredCustomerPaymentAttempt's docstring.
  }
}

/** Removes any stored attempt for (companyId, invoiceId) -- called on
 * success, definite rejection, closed-period, and idempotency-conflict. */
export function clearStoredCustomerPaymentAttempt(
  companyId: number,
  invoiceId: number,
): void {
  try {
    sessionStorage.removeItem(customerPaymentAttemptStorageKey(companyId, invoiceId));
  } catch {
    // Best-effort -- see readStoredCustomerPaymentAttempt's docstring.
  }
}

/**
 * Reconstructs a full in-memory CustomerPaymentAttempt from a stored
 * (persisted) record, for restoring on mount/reload or on an invoice
 * change. `submittedPayload` is always null here -- it was never
 * persisted (see StoredCustomerPaymentAttempt's docstring) -- so a
 * restored attempt can be COMPARED against (via its fingerprint) but
 * cannot be used to pre-fill form fields. This is intentional: the
 * fingerprint comparison alone is what guarantees safety (an edited retry
 * is blocked either way); pre-filling the form after a reload is a
 * usability nicety this patch does not attempt, since there is nothing
 * left to restore it from.
 */
export function attemptFromStored(
  stored: StoredCustomerPaymentAttempt,
): CustomerPaymentAttempt {
  return {
    state: "ambiguous",
    invoiceId: stored.invoiceId,
    idempotencyKey: stored.idempotencyKey,
    submittedFingerprint: stored.fingerprint,
    submittedPayload: null,
  };
}

// ---------------------------------------------------------------------------
// User-facing messages
// ---------------------------------------------------------------------------

/**
 * Shown when an edited retry is blocked while an ambiguous attempt for
 * THIS invoice remains unresolved (including after a reload -- the
 * fingerprint comparison behaves identically whether the attempt came
 * from the in-memory ref or was just restored from sessionStorage).
 * Deliberately does NOT claim reloading alone is safe, and does NOT try to
 * infer whether the original request completed -- only the backend knows
 * that. Must convey: (1) the previous result is uncertain, (2) the user
 * can retry safely only by resubmitting the exact same details, (3)
 * otherwise they should verify the invoice's payment history first.
 */
export const CUSTOMER_PAYMENT_AMBIGUOUS_RETRY_BLOCKED_MESSAGE =
  "The result of your last payment attempt on this invoice is uncertain. " +
  "Retry with the exact same amount, date, account, method, and fee " +
  "choice/override to safely resolve it, or check this invoice's payment " +
  "history before changing any details. (An automatic fee has no typed value " +
  "to match -- only the same automatic-vs-manual choice.)";

/**
 * Shown when the retained ambiguous attempt was minted by an EARLIER app
 * version (old-shape fingerprint, no fee_mode discriminator). Such an attempt
 * can never be matched by a current-shape retry -- even with identical visible
 * details -- so, unlike the ambiguous-retry message, this deliberately does NOT
 * suggest that retrying unchanged will resolve it. It directs the user to
 * verify history before deciding. The stored key/fingerprint are left intact
 * (never rebound, cleared, or replaced with a fresh key).
 */
export const CUSTOMER_PAYMENT_LEGACY_SHAPE_BLOCKED_MESSAGE =
  "Your last payment attempt on this invoice was started in an earlier version " +
  "of the app, so its result can't be confirmed or safely retried from here. " +
  "Check this invoice's payment history to see whether that payment was already " +
  "recorded before starting a new one.";

/**
 * Shown for an idempotency-conflict 409 (the plain request-identity
 * conflict, or -- structurally unreachable from this manual caller, see
 * classifyCustomerPaymentFailure's docstring -- a reconciliation
 * conflict): the retained key is permanently bound to a different,
 * already-recorded payment and can never succeed for the current form
 * values. Deliberately avoids internal reconciliation terminology
 * ("verified Stripe", "reconciliation set") -- the user only needs to
 * know their view of this invoice's payments may be stale.
 */
export const CUSTOMER_PAYMENT_CONFLICT_MESSAGE =
  "This payment attempt no longer matches a payment already recorded for " +
  "this invoice. Please refresh this invoice's payment status before " +
  "trying again.";

/** Shown when a submit is attempted while another one for this invoice is
 * still in flight. */
export const CUSTOMER_PAYMENT_IN_FLIGHT_MESSAGE =
  "A payment is already being submitted for this invoice.";

/**
 * Shown when a same-key retry is blocked because the retained ambiguous
 * attempt belongs to a DIFFERENT invoice (e.g. the user navigated to
 * another Sale while an attempt on a previous one was still unresolved --
 * SaleDetail.tsx is reused across Sale/Invoice ids without remounting).
 */
export const CUSTOMER_PAYMENT_CROSS_INVOICE_BLOCKED_MESSAGE =
  "A payment attempt on a different invoice has not been confirmed yet. " +
  "Please return to that invoice and retry before starting a new payment here.";
